import { Checkbox, FormGroup, Input, Text } from '@agro-club/agroclub-shared'
import { useAnalyticsClickHandler } from 'analytics/hooks'
import { FormikProps } from 'formik'
import { endpoints } from 'modules/endpoints'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormSelectButton } from 'views/components/FormSelectButtons/FormSelectButtons'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { ShortFormControl } from 'views/styled/common'
import FormSelectShared from '../../../../components/FormSelectShared'
import { AddCarrierModal } from '../../CarriersKR/AddCarrierModal/AddCarrierModal'
import { AddNewCarModal } from '../../LogisticRequests/RequestsBoard/OpenRequestsBoardColumns/DraftTrips/AddNewCarModal'
import { DraftTripsInitial } from '../../LogisticRequests/RequestsBoard/OpenRequestsBoardColumns/DraftTrips/types'
import { DriverAddModal } from '../DriverModals/DriverAddModal'
import { TrailerSelect } from '../TrailerSelect/TrailerSelect'
import { MarginWrapper } from './styled'
import { Carrier } from 'modules/domain/logisticsKR/types'
import { CarrierOption } from './CarrierOption'
import { Driver } from '../../types'
import { DriverDocuments } from '../../CarriersKR/CarrierKRDetails/CarrierAccordion/DriversDocuments/DriverDocuments'

interface Props {
  formik: FormikProps<DraftTripsInitial>
  controlsDisable: Record<string, boolean> | undefined
}

export const CarrierControls: FC<Props> = ({ formik, controlsDisable }) => {
  const { t } = useTranslation('logisticBids')
  const [addCarrierModal, setAddCarrierModal] = useState(false)
  const [addCarModal, setAddCarModal] = useState(false)
  const [addDriverModal, setAddDriverModal] = useState(false)
  const handleClick = useAnalyticsClickHandler('add')
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null)
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)

  const handleChangeCarrier = () => {
    formik.setFieldValue('car', '')
    formik.setFieldValue('trailer', '')
    formik.setFieldValue('driver', '')
  }

  useEffect(() => {
    if (!selectedCarrier || !selectedDriver) return
    if (selectedCarrier.name.includes(selectedDriver.name)) {
      formik.setFieldValue('is_driver_a_carrier', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarrier, selectedDriver])

  return (
    <>
      <HrWithHeader text={t('form.block_titles.carrier_info')} />
      <div>
        <FormSelectShared
          OptionComponent={CarrierOption}
          onChange={handleChangeCarrier}
          endpoint={endpoints.LogisticsKR.carriers()}
          placeholder={''}
          getLabel={(dto) => dto.name}
          label={t('form.labels.carrier')}
          fieldName="carrier"
          disabled={controlsDisable?.carrier}
          formik={formik}
          searchField="search"
          onSelectedOptionLoaded={(e) => setSelectedCarrier(e.dto)}
          required
        >
          <FormSelectButton
            action={handleClick(() => setAddCarrierModal(true))}
            eventId="addCarrier"
            disabled={controlsDisable?.carrier}
          />
        </FormSelectShared>
        <MarginWrapper>
          <Checkbox
            isChecked={formik.values.need_contract}
            onChange={(_value, isChecked) => formik.setFieldValue('need_contract', isChecked)}
            label={t('form.labels.need_contract')}
          />
          <Text color="secondary">
            {formik.values.carrier && selectedCarrier?.inn ? `ИНН: ${selectedCarrier.inn}` : ''}
          </Text>
        </MarginWrapper>
      </div>
      {addCarrierModal && (
        <AddCarrierModal
          onClose={() => setAddCarrierModal(false)}
          onSuccess={(val: Carrier) => {
            formik.setFieldValue('carrier', val.id)
            formik.setFieldValue('carrier_phone', val.phone)
          }}
        />
      )}
      <ShortFormControl>
        <FormSelectShared
          endpoint={endpoints.LogisticsKR.vatValues()}
          label={t('form.labels.vat')}
          simpleApiFormat
          getLabel={(dto) => dto.label}
          showSearch={false}
          placeholder=""
          fieldName="vat_value"
          formik={formik}
        />
        <FormGroup label={t('form.labels.phone')} error={formik.errors.carrier_phone}>
          <Input
            placeholder={t('common:phone_placeholder')}
            {...formik.getFieldProps('carrier_phone')}
            invalid={!!formik.errors.carrier_phone}
          />
        </FormGroup>
      </ShortFormControl>
      <div>
        <FormSelectShared
          formik={formik}
          endpoint={endpoints.LogisticsKR.carrierCars(formik.values.carrier)}
          fieldName="car"
          searchField="search"
          label={t('form.labels.car')}
          placeholder=""
          getLabel={(dto) => `${dto.brand_model} / ${dto.licence_number}`}
          disabled={controlsDisable?.carrier || controlsDisable?.car}
          required
        >
          <FormSelectButton
            action={handleClick(() => setAddCarModal(true))}
            eventId="addCar"
            disabled={controlsDisable?.carrier || controlsDisable?.car || !formik.values.carrier}
          />
        </FormSelectShared>

        {addCarModal && (
          <AddNewCarModal
            onClose={() => setAddCarModal(false)}
            carrierId={formik.values?.carrier}
            setValue={(val) => formik.setFieldValue('car', String(val))}
          />
        )}
      </div>
      <div>
        <TrailerSelect formik={formik} carrierId={formik.values.carrier} trailerInfoRequired />
      </div>
      <HrWithHeader text={t('form.block_titles.driver_info')} />
      <div>
        <FormSelectShared
          formik={formik}
          fieldName="driver"
          endpoint={endpoints.LogisticsKR.driversByCarrier(formik.values.carrier)}
          label={t('form.labels.driver')}
          placeholder=""
          getLabel={(dto) => dto.name}
          searchField="search"
          onSelectedOptionLoaded={(e) => setSelectedDriver(e.dto)}
          required
        >
          <FormSelectButton action={handleClick(() => setAddDriverModal(true))} eventId="addDriver" />
        </FormSelectShared>
        <MarginWrapper>
          <Checkbox
            isChecked={formik.values.is_driver_a_carrier}
            onChange={(_value, isChecked) => formik.setFieldValue('is_driver_a_carrier', isChecked)}
            label={t('form.labels.is_driver_a_carrier')}
          />
        </MarginWrapper>
        {addDriverModal && (
          <DriverAddModal
            carrierId={formik.values.carrier}
            onSuccess={(id) => formik.setFieldValue('driver', String(id))}
            setIsOpen={setAddDriverModal}
          />
        )}
      </div>
      {selectedDriver && formik.values.driver && (
        <FormGroup error={formik.errors.driver_documents} label={t('form.labels.documents')} required>
          <DriverDocuments driverId={selectedDriver.id} driver={selectedDriver} maxWidth={400} />
        </FormGroup>
      )}
    </>
  )
}
